import React, { isValidElement, useState } from "react";
import "../Table/CommonTable.scss";
import { DEVICE_ICON_CLASS, getDeviceIcon } from "../../utils/deviceIcons";
import userAvatar from "../../assets/images/user1.png";
import ReactPaginate from "react-paginate";
function CommonTable({
  columns,
  rows,
  onItemsPerPageChange,
  itemsPerPage,
  totalPages,
  currentPage,
  onPageChange,
  isPagination,
  pageName,
  scroll,
  sortBy,
  setSortBy,
  setSortType,
  onTripSelect,
  emptyState,
  showHeaderWhenEmpty = false,
  hidePaginationWhenEmpty = false,
  totalCount,
  getRowClassName,
}) {
  const [clickCount, setClickCount] = useState(1);
  
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const handleSorting = (sortby) => {
    if (setSortBy && setSortType) {
      if (sortBy !== sortby) {
        setClickCount(1);
        setSortType("ASC")
        setSortBy(sortby);
        return;
      }
  
      if(clickCount === 1){
        setSortType("ASC")
        setSortBy(sortby);
        setClickCount(2);
      }else if (clickCount === 2) {
        setSortType("DSC")
        setSortBy(sortBy);
        setClickCount(3);
      }else {
        setSortType("")
        setSortBy("")
        setClickCount(1);
      }
    }
  }
  
  const handlePageChange = ({ selected }) => {
   const newPage = selected + 1;
    if (newPage === currentPage) return;
    onPageChange(newPage);
  };

  const isSubscriptionPage = pageName === "subscription";

  const getClassName = (colName) => {
    if (colName?.includes("location")) {
      return "location_row_data";
    }
    if (isSubscriptionPage) {
      if (colName === "planType") return "subscription-type-col";
      if (colName === "planName") return "plan_row_data";
      if (colName === "priceSummary") return "price_row_data plan-catalog-price-cell";
      if (colName === "description") return "Sub_description_row_data plan-catalog-description-cell";
      if (colName === "action") return "action_row_data";
    }
    if (colName?.includes("planName") && isSubscriptionPage) {
      return "plan_row_data";
    }
    if (colName?.includes("price") && isSubscriptionPage) {
      return "price_row_data";
    }
    if (colName?.includes("action") && isSubscriptionPage) {
      return "action_row_data";
    }
    return colName === "description"
      ? (isSubscriptionPage ? "Sub_description_row_data plan-catalog-description-cell" : "description_row_data")
      : "row_data_text";
  };

  const getHeaderAlignClass = (col, index) => {
    if (isSubscriptionPage) {
      if (col.key === "action") return "text-center";
      if (col.key === "priceSummary") return "text-end";
      return "text-start";
    }
    if (col?.key?.includes("action")) return "text-center";
    return index === 0 ? "" : "text-center";
  };

  const getCellAlignClass = (col, index) => {
    if (isSubscriptionPage) {
      if (col.key === "action") return "text-center";
      if (col.key === "priceSummary") return "text-end";
      return "text-start";
    }
    if (col?.key?.includes("action")) return "text-center";
    return index === 0 ? "" : "text-center";
  };
  const rowCount = rows?.length ?? 0;
  const resultTotal = totalCount ?? rowCount;
  const hasResults = resultTotal > 0;
  const effectiveTotalPages = totalPages > 0 ? totalPages : 0;

  const renderPagination = () => {
    if (hidePaginationWhenEmpty && !hasResults) {
      return (
        <div className="pagination-container pagination-container--empty">
          <span className="pagination-result-summary">0 results</span>
        </div>
      );
    }

    return (
      <div className="pagination-container">
        <div className="pagination-left">
          <label className="items-per-page-label" htmlFor={`items-per-page-${pageName || "table"}`}>
            Rows per page
          </label>
          <select
            id={`items-per-page-${pageName || "table"}`}
            className="items-per-page"
            onChange={onItemsPerPageChange}
            value={itemsPerPage}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          {resultTotal > 0 && (
            <span className="pagination-result-summary">
              {resultTotal} {resultTotal === 1 ? "result" : "results"}
            </span>
          )}
        </div>
        {effectiveTotalPages > 1 ? (
          <ReactPaginate
            previousLabel={"Previous"}
            nextLabel={"Next"}
            breakLabel={"..."}
            breakClassName={"pagination-break"}
            pageCount={effectiveTotalPages}
            marginPagesDisplayed={2}
            pageRangeDisplayed={3}
            onPageChange={handlePageChange}
            containerClassName={"pagination"}
            activeClassName={"active"}
            previousClassName={`pagination-button ${currentPage === 1 ? "hidden" : ""}`}
            nextClassName={`pagination-button ${currentPage === effectiveTotalPages ? "hidden" : ""}`}
            pageClassName={"pagination-button"}
            disabledClassName={"disabled"}
            forcePage={currentPage - 1}
          />
        ) : hasResults ? (
          <div className="pagination">
            <div className="pagination-button active">1</div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderTableHead = () => (
    <thead>
      <tr>
        {columns?.map((col, index) => (
          col?.key?.includes("action") ? (
            <th key={`column_${index}`} className="text-center" scope="col">
              Action
            </th>
          ) : (
            <th
              key={`column_${index}`}
              className={getHeaderAlignClass(col, index)}
              scope="col"
              onClick={() => handleSorting(col.key)}
            >
              {col?.header}
            </th>
          )
        ))}
      </tr>
    </thead>
  );

  const renderEmptyBody = () => (
    <tbody>
      <tr className="table-empty-row">
        <td colSpan={columns?.length || 1}>
          <div className="table-empty-state">
            <p className="table-empty-state__title">
              {emptyState?.title || "No Data Found"}
            </p>
            {emptyState?.message && (
              <p className="table-empty-state__message">{emptyState.message}</p>
            )}
            {emptyState?.actionLabel && emptyState?.onAction && (
              <button
                type="button"
                className="btn btn-link table-empty-state__action"
                onClick={emptyState.onAction}
              >
                {emptyState.actionLabel}
              </button>
            )}
          </div>
        </td>
      </tr>
    </tbody>
  );
  return (
    <>
      <div className={`common-table-block table-responsive ${scroll ? "scrollbar" : ""} ${pageName ? `common-table-block--${pageName}` : ""}`}>
        <table className="table">
          {rowCount > 0 || showHeaderWhenEmpty ? (
            <>
              {renderTableHead()}
              {rowCount > 0 ? (
              <tbody>
                {rows?.map((row, index) => {
                  const rowClassName = getRowClassName ? getRowClassName(row) : "";
                  return (
                    <tr key={`row_${index}`} className={rowClassName || undefined}>
                      {columns?.map((col, idx) => {
                        return (
                          <td className={`${getClassName(col?.key)}`} key={`row_data_${index}_${col.key}`}>
                            <div
                              className={`table_row_data ${getCellAlignClass(col, idx)} ${
                                col?.key === "description" && !isSubscriptionPage
                                  ? "d-flex align-items-center justify-content-center"
                                  : ""
                              } ${
                                col?.key === "description" && isSubscriptionPage
                                  ? "d-flex align-items-start justify-content-start"
                                  : ""
                              }`}
                            >
                              {col?.key === "action" || col?.key === "planType" ? (
                                <>{row[col.key]}</>
                              ) : col?.key === "device" ? (
                                <div className={`device_row_data ${(onTripSelect)?`cursor-pointer`:``}`} onClick={()=>{if(onTripSelect){onTripSelect(row)}}}>
                                  <img
                                    className={DEVICE_ICON_CLASS}
                                    src={getDeviceIcon(row.deviceCategory)}
                                    alt=""
                                  />
                                  <p className="row_data_text">
                                    {row[col.key]}
                                  </p>
                                </div>
                              ) : col?.key === "status" ? (
                                <div className="status_data">
                                  <p
                                    className={`${row[
                                      col?.key
                                    ]?.toLowerCase()}-status`}
                                  >
                                    {row[col.key]}
                                  </p>
                                </div>
                              )
                              : col?.key === "date" ? (
                                <p className="row_data_text">
                                  {formatDate(row[col.key])}
                                </p>
                              )
                               : row[col.key]?.img !== undefined ? (
                                <div className="user_image_data"  >
                                  <div className="user-image">
                                    <img
                                      src={row[col.key]?.img}
                                      className="img-fluid"
                                      alt="user-profile eee"
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = userAvatar;
                                      }}
                                    />
                                  </div>
                                  <div className="row_data_text 333">
                                    {row[col.key]?.value}
                                  </div>
                                </div>
                              ) : row[col.key]?.value !== undefined ? (
                                <p className="row_data_text 32">
                                  {row[col.key]?.value}
                                </p>
                              ) : isValidElement(row[col.key]) ? (
                                <div className="table-cell-custom">{row[col.key]}</div>
                              ) : (
                                <p className={`${getClassName(col?.key)} 232`}>
                                  {row[col.key]}
                                </p>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
              ) : (
                renderEmptyBody()
              )}
            </>
          ) : (
            <tbody>
              <tr className="table-empty-row">
                <td>
                  <span className="d-flex justify-content-center no_data_found">No Data Found</span>
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>

      {isPagination && renderPagination()}
    </>
  );
}
export default CommonTable;
